import { ApiError } from './api/ApiError';
import { ERROR_CODES, type ErrorCode } from './api/error-codes';
import { hasKey, t, tn } from './i18n';

/**
 * `code` → mensagem pt-BR (FR-123, matriz do PRD §6.3 e
 * `specs/AIM-POC/contracts/api-backend.md › Erros`).
 *
 * Regra: a UI decide sempre por `code`, nunca pela `message` do servidor.
 * A mensagem exibida é `mensagem (código)` — o código técnico entre parênteses —
 * mais "Nada foi gravado." quando a operação era de escrita (FR-123).
 *
 * A lista de códigos é gerada de `api-backend.md` por `pnpm gen:api`
 * (`api/error-codes.ts`); `__tests__/errors.test.ts` falha se algum ficar sem
 * mensagem aqui.
 */

/** O que a tela deve fazer depois de mostrar o erro. */
export type ErrorEffect =
  /** Volta ao formulário mantendo o que foi digitado. */
  | 'formulario'
  /** Recarrega o registro e descarta a tentativa (FR-124). */
  | 'recarregar'
  /** Manda o usuário à tela de login. */
  | 'login'
  /** Discovery marca a release; não há ação do usuário no formulário. */
  | 'marcar_release'
  /** Só informa. */
  | 'informar';

/**
 * O que o código diz sobre gravação (FR-123):
 *
 * - `nada` — a ordem falhou inteira: "Nada foi gravado." vale mesmo que quem
 *   chamou não tenha marcado `escrita`;
 * - `parcial` — o middleware pode ter deixado estado atrás: **nunca** se diz
 *   "nada foi gravado", porque algo pode ter sido.
 *
 * Ausente = decide pelo `escrita` de quem chamou.
 */
export type Write = 'nada' | 'parcial';

/**
 * Comportamento de um `code` — **sem texto**. É esta a separação que a B1b traz:
 * o que a tela FAZ com o erro (para onde manda, se recarrega, se diz "nada foi
 * gravado") é lógica e fica aqui; o que a tela DIZ é catálogo e vive em
 * `i18n/locales/<locale>/error.json`, uma entrada por `code`.
 *
 * Antes as duas coisas moravam na mesma struct, e por isso traduzir o produto
 * exigia mexer em lógica de UI.
 */
export interface ErrorBehavior {
  efeito: ErrorEffect;
  gravacao?: Write;
  /** Rota do link corretivo; o rótulo sai do catálogo (`<code>.linkLabel`). */
  rota?: string;
  codigoNaFrase?: boolean;
}

/** Espec resolvida: comportamento + as frases já buscadas no catálogo. */
export interface ErrorSpec {
  mensagem: string;
  /**
   * Alternativa usada quando os marcadores `{x}` de `mensagem` não puderam ser
   * preenchidos — sem ela o usuário leria o template cru na tela.
   */
  mensagemSemDados?: string;
  efeito: ErrorEffect;
  gravacao?: Write;
  /** Orientação extra, sempre depois da frase de gravação. */
  sufixo?: string;
  /** Link sugerido para a ação corretiva. */
  link?: { rota: string; rotulo: string };
  /**
   * `true` quando a própria `mensagem` nomeia o código — só a frase montada para
   * código fora da tabela faz isso. Quem exibe usa a marca para não repetir o
   * `(code)` na mesma linha.
   *
   * É uma marca, e não uma busca por substring na frase pronta: `valor_invalido`
   * é um código conhecido cuja `message` do middleware pode conter a palavra
   * ("Falha (config_valor_invalido)"), e o teste por texto escondia o código
   * justamente onde ele precisava aparecer.
   */
  codigoNaFrase?: boolean;
}

const T15 = '/runtime';
const T20 = '/infra';
const T21 = '/tenant';
const T6 = '/resources';

/** Sufixo de escrita: toda falha de escrita com o middleware não gravou nada (Artigo II). */
export function nothingStored(): string {
  return t('common.nothingStored');
}

/** Orientação do `estado_parcial` (FR-123, última linha da matriz). */
export function partialStateSuffix(): string {
  return t('error.estado_parcial.suffix');
}

export const ERROR_BEHAVIOR: Record<ErrorCode, ErrorBehavior> = {
  apim_id_duplicado: { efeito: 'formulario' },
  apim_ilegivel: { efeito: 'formulario', rota: T20 },
  apim_nao_configurado: { efeito: 'formulario', rota: T20 },
  apim_nao_pronto: { efeito: 'informar', rota: T20 },
  app_desconhecida_na_politica: { efeito: 'formulario' },
  app_inexistente: { efeito: 'formulario', rota: T15 },
  aprovacao_nao_pendente: { efeito: 'recarregar' },
  atribuicao_incompleta: { efeito: 'formulario' },
  basename_duplicado: { efeito: 'formulario' },
  borda_recusou: { efeito: 'informar', rota: T21 },
  campo_desconhecido: { efeito: 'formulario' },
  catalogo_indisponivel: { efeito: 'formulario', rota: T20 },
  // Chat «Ask» (04/09): a forma de «quem pergunta» é da tela — volta ao formulário.
  claims_invalidas: { efeito: 'formulario' },
  cluster_inalcancavel: { efeito: 'informar', rota: T20 },
  cluster_nao_configurado: { efeito: 'formulario', rota: T20 },
  configmap_falhou: { efeito: 'formulario', gravacao: 'nada' },
  conta_nao_autorizada: { efeito: 'login' },
  container_inexistente: { efeito: 'formulario' },
  contrato_inexistente: { efeito: 'formulario', rota: T15 },
  credenciais_invalidas: { efeito: 'formulario' },
  dados_invalidos: { efeito: 'formulario' },
  deployment_inexistente: { efeito: 'formulario' },
  detalhe_indisponivel: { efeito: 'informar' },
  digest_inexistente: { efeito: 'marcar_release' },
  digest_invalido: { efeito: 'formulario' },
  discovery_em_execucao: { efeito: 'informar' },
  email_duplicado: { efeito: 'formulario' },
  erro_interno: { efeito: 'informar' },
  estado_parcial: { efeito: 'informar', gravacao: 'parcial', rota: T15 },
  google_estado_invalido: { efeito: 'login' },
  google_falhou: { efeito: 'login' },
  google_nao_configurado: { efeito: 'informar', rota: T21 },
  http_500: { efeito: 'formulario' },
  identidade_congelada: { efeito: 'formulario' },
  imagem_fora_do_registry: { efeito: 'formulario', rota: T20 },
  indisponivel: { efeito: 'informar' },
  label_ausente: { efeito: 'marcar_release' },
  // Chat «Ask»: o pod sem chave de modelo. Só informa — quem resolve é quem
  // opera o lab (Secret `agents-llm`), não a tela.
  llm_disabled: { efeito: 'informar' },
  limite_nao_aceito: { efeito: 'formulario', rota: T6 },
  login_bloqueado: { efeito: 'formulario' },
  materializacao_recusada: { efeito: 'informar', gravacao: 'nada', rota: T15 },
  middleware_fora: { efeito: 'informar' },
  middleware_nao_configurado: { efeito: 'informar', rota: T21 },
  middleware_sem_resposta: { efeito: 'informar' },
  // Chat «Ask»: o provedor do modelo não respondeu — tentar de novo é a ação.
  model_unreachable: { efeito: 'formulario' },
  modulo_inexistente: { efeito: 'recarregar' },
  nao_autenticado: { efeito: 'login' },
  nao_encontrado: { efeito: 'recarregar' },
  nao_implementado: { efeito: 'informar' },
  nome_duplicado: { efeito: 'formulario' },
  origem_nao_permitida: { efeito: 'formulario', rota: T20 },
  papel_duplicado: { efeito: 'formulario' },
  papel_em_uso: { efeito: 'informar' },
  patch_falhou: { efeito: 'formulario' },
  pdp_inalcancavel: { efeito: 'formulario', gravacao: 'nada' },
  // Chat «Ask»: pessoa fora do elenco no `/ask` do middleware.
  pessoa_desconhecida: { efeito: 'formulario' },
  pdp_nao_configurado: { efeito: 'formulario', rota: T20 },
  pdp_recusou: { efeito: 'formulario', gravacao: 'nada' },
  recurso_inexistente: { efeito: 'formulario', rota: T6 },
  registry_ilegivel: { efeito: 'marcar_release' },
  rego_indisponivel: { efeito: 'informar' },
  // P5 — pedir reconciliacao de admissao num site em postura L1/L2. Nada foi
  // gravado, e nao ha acao corretiva na tela de origem: quem quiser a admissao
  // de volta muda a postura em Configuracoes.
  postura_sem_vinculo_de_artefato: { efeito: 'informar', gravacao: 'nada' },
  // P1 — as duas recusas do «Excluir site». `informar`, e nao `formulario`: nao ha
  // campo a corrigir. A acao corretiva e outra (remover as identidades e os
  // recursos) ou nao existe (site padrao), e `gravacao: 'nada'` porque a exclusao
  // nem chegou a comecar.
  // aim-xsk.8 — a admissão no cluster. Nenhuma tem campo a corrigir no formulário da página (o token e o endereço
  // ficam como estão, que é o efeito de `informar`); `gravacao` fica com quem chama, porque a mesma recusa vem de
  // um registro (nada gravado) e de uma releitura (que grava o erro observado).
  admission_certificate_mismatch: { efeito: 'informar' },
  admission_not_registered: { efeito: 'informar', gravacao: 'nada' },
  admission_refused: { efeito: 'informar' },
  admission_token_expired: { efeito: 'informar', gravacao: 'nada' },
  admission_token_invalid: { efeito: 'informar', gravacao: 'nada' },
  admission_unreachable: { efeito: 'informar' },
  site_padrao: { efeito: 'informar', gravacao: 'nada' },
  site_em_uso: { efeito: 'informar', gravacao: 'nada' },
  // Lote 3 · F1-b — «Excluir ambiente». Mesma forma das duas acima, e pela mesma
  // razao: nao ha campo a corrigir (a acao corretiva e mover as identidades e os
  // recursos, ou marcar outro padrao) e `gravacao: 'nada'` porque a exclusao nem
  // chegou a comecar — a recusa acontece antes da transacao.
  environment_in_use: { efeito: 'informar', gravacao: 'nada' },
  // Mesmo COMPORTAMENTO do irmao (nao ha campo a corrigir; a exclusao nem comecou) e
  // codigo SEPARADO — o que muda e a FRASE, e e ela que diz a saida: marcar outro
  // padrao, em vez de mover quem aponta.
  environment_is_default: { efeito: 'informar', gravacao: 'nada' },
  // Fase 2 (aim-co1.3) — o recurso agente. Nenhum dos três tem campo a corrigir e nenhum começou a gravar: a
  // saída está no gateway (remover a rota ou a operação) ou no próprio agente (religar a outra identidade).
  agent_resource_not_removable: { efeito: 'informar', gravacao: 'nada' },
  agent_route_not_served_by_identity: { efeito: 'informar', gravacao: 'nada' },
  agent_relink_only_internal: { efeito: 'informar', gravacao: 'nada' },
  // aim-5wc — the chat entry point. The four target refusals point at the Service field of the drawer; the
  // unreadable secret is fixed by replacing it in the drawer; the two AG-UI ends arrive in the answer, not as
  // errors, and only inform. None of them wrote anything: the executor refuses before calling.
  chat_entry_secret_unreadable: { efeito: 'informar', gravacao: 'nada' },
  chat_entry_namespace_not_allowed: { efeito: 'formulario', gravacao: 'nada' },
  chat_entry_service_not_found: { efeito: 'formulario', gravacao: 'nada' },
  chat_entry_not_served_by_identity: { efeito: 'formulario', gravacao: 'nada' },
  chat_entry_service_mixed: { efeito: 'formulario', gravacao: 'nada' },
  agent_run_error: { efeito: 'informar' },
  agent_stream_incomplete: { efeito: 'informar' },
  rego_nao_validado: { efeito: 'formulario' },
  requisicao_invalida: { efeito: 'formulario' },
  rota_ausente: { efeito: 'informar', rota: T20 },
  rota_desativada: { efeito: 'informar', gravacao: 'nada' },
  sem_contrato_ativo: { efeito: 'informar' },
  sem_permissao: { efeito: 'informar' },
  serviceaccount_falhou: { efeito: 'informar', rota: T15 },
  sessao_expirada: { efeito: 'login' },
  slug_duplicado: { efeito: 'formulario' },
  slug_rename_only_in_drawer: { efeito: 'informar', gravacao: 'nada' },
  slug_nao_permitido: { efeito: 'informar' },
  tool_em_uso: { efeito: 'informar' },
  // Guarda temporaria da P1: o formulario fica como esta (FR-123) e a pessoa
  // corrige o campo. A ROTA nao aponta para Sites de proposito: o caminho de saida
  // nao e mexer no site; mandar para outra tela seria sugerir um conserto que nao
  // conserta. Era o codigo do nome no PDP ate a D2 do aim-cpj (09/10).
  tool_in_another_site: { efeito: 'formulario' },
  tool_nao_catalogada: { efeito: 'formulario', rota: T6 },
  // aim-cpj — o Simulador (T18) com uma tool sem servidor que está em dois servidores do contrato. A pessoa corrige
  // o pedido (escolhe o par na lista), e tentar de novo igual dá a mesma recusa; nada foi gravado.
  tool_server_ambiguous: { efeito: 'formulario', gravacao: 'nada' },
  ultimo_admin: { efeito: 'informar' },
  valor_invalido: { efeito: 'formulario' },
  versao_antiga: { efeito: 'recarregar' },
  versao_antiga_executor: { efeito: 'recarregar', gravacao: 'nada' },
  write_guard: { efeito: 'formulario', gravacao: 'nada' },
};

export const CLIENT_ERROR_BEHAVIOR: Record<ClientErrorCode, ErrorBehavior> = {
  sem_conexao: { efeito: 'informar' },
  tempo_esgotado: { efeito: 'informar' },
  resposta_invalida: { efeito: 'informar' },
  erro_desconhecido: { efeito: 'informar' },
};

/** Códigos que só o cliente HTTP produz (`client.ts`) — não vêm do backend. */
export const CLIENT_ERROR_CODES = [
  'sem_conexao',
  'tempo_esgotado',
  'resposta_invalida',
  'erro_desconhecido',
] as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];

const ALL_BEHAVIOR: Record<string, ErrorBehavior> = {
  ...ERROR_BEHAVIOR,
  ...CLIENT_ERROR_BEHAVIOR,
};

/**
 * Monta a espec de um código conhecido: comportamento daqui, frases do catálogo.
 * As chaves opcionais só entram se existirem — `hasKey` no locale base, para que
 * um sufixo ausente não vire a string "error.x.suffix" na tela.
 */
function specOf(code: string, behavior: ErrorBehavior): ErrorSpec {
  const noData = `error.${code}.noData`;
  const suffix = `error.${code}.suffix`;
  return {
    mensagem: t(`error.${code}`),
    ...(hasKey(noData) ? { mensagemSemDados: t(noData) } : {}),
    ...(hasKey(suffix) ? { sufixo: t(suffix) } : {}),
    efeito: behavior.efeito,
    ...(behavior.gravacao ? { gravacao: behavior.gravacao } : {}),
    ...(behavior.rota
      ? { link: { rota: behavior.rota, rotulo: t(`error.${code}.linkLabel`) } }
      : {}),
    ...(behavior.codigoNaFrase ? { codigoNaFrase: true } : {}),
  };
}

const FALLBACK = (): ErrorSpec =>
  specOf('erro_desconhecido', CLIENT_ERROR_BEHAVIOR.erro_desconhecido);

/** Todos os códigos com mensagem (taxonomia do contrato + os do cliente). */
export const KNOWN_ERROR_CODES: readonly string[] = [...ERROR_CODES, ...CLIENT_ERROR_CODES];

/**
 * Prefixo dos códigos que o cliente do middleware **forma** por status, quando a
 * resposta ≥ 400 não trouxe `detail.erro` nem passo com código (regra de extração,
 * `middleware-ordens.md` §0, passo 4). Só `http_500` tem linha no contrato; o
 * resto da família nasce a qualquer momento e não pode nascer mudo.
 */
const PREFIX_HTTP = 'http_';

/** Ponto final quando o texto do servidor não trouxe pontuação — a frase da tela é uma frase. */
function pontuar(texto: string): string {
  return /[.!?…]$/.test(texto) ? texto : `${texto}.`;
}

/**
 * Espec para um `code` que a tabela não tem. Duas situações reais, e nas duas o
 * servidor **disse** o que houve — a tela não pode responder uma vaguidade por
 * cima disso (`api-backend.md › Erros` › "Código que a tabela não tem"):
 *
 *  - código do executor mais novo que esta tabela — `rota_desativada` e
 *    `cluster_inalcancavel` eram exatamente isso até 28/08, e enquanto foram, a
 *    tela mostrava a mesma frase que mostra para um `catch` vazio;
 *  - `http_<status>`, formado pelo cliente do middleware.
 *
 * `null` = não há nada do servidor para mostrar; aí o FALLBACK é honesto.
 */
function newCodeEspec(code: string, serverMessage?: string): ErrorSpec | null {
  const mensagem = (serverMessage ?? '').trim();

  if (mensagem) {
    return {
      mensagem: t('error.unknownWithServerMessage', { code, message: pontuar(mensagem) }),
      efeito: 'informar',
      codigoNaFrase: true,
    };
  }

  if (code.startsWith(PREFIX_HTTP)) {
    return {
      mensagem: t('error.unknownHttpStatus', { status: code.slice(PREFIX_HTTP.length) }),
      efeito: 'informar',
    };
  }

  return null;
}

/**
 * Texto para um `code`. `mensagemDoServidor` é o `message` do envelope: sem ela,
 * código fora da tabela não tem como se explicar e cai no genérico — é por isso
 * que `errorView` sempre a repassa.
 */
export function errorSpec(code: string, serverMessage?: string): ErrorSpec {
  const behavior = ALL_BEHAVIOR[code];
  if (behavior) return specOf(code, behavior);
  return newCodeEspec(code, serverMessage) ?? FALLBACK();
}

/**
 * Frase de um item de `data.campos[]` (B1b): o backend manda `codigo` + `params`
 * e quem monta a frase é o catálogo.
 *
 * O fallback para `mensagem` não é preguiça — é a rede de duas situações reais:
 * um backend mais velho que este catálogo (durante a transição) e um `codigo`
 * que o catálogo ainda não tem. Nos dois casos o servidor **disse** algo, e
 * engolir isso por uma frase genérica é pior do que mostrar o que veio.
 */
export function fieldMessage(campo: {
  code?: string;
  params?: Record<string, string>;
  message?: string;
}): string {
  const key = campo.code ? `field.${campo.code}` : '';
  if (key && hasKey(key)) return t(key, campo.params);
  return campo.message ?? key;
}

/** `true` quando o código tem texto **próprio** na tabela (não a frase montada do servidor). */
export function hasErrorMessage(code: string): boolean {
  return code in ALL_BEHAVIOR;
}

/**
 * Uma contagem do `ErroData` como FRASE do catálogo, ou `null` quando ela não veio
 * ou é zero.
 *
 * Zero devolve `null` de propósito: quem escolhe a variante da frase (`variantOf`)
 * só a escolhe quando a contagem é positiva, e assim um `{identities}` que sobrasse
 * numa frase por engano degrada em vez de escrever «0 identidades» — que lê como
 * defeito para quem está a ver a tela.
 */
function contagem(valor: number | undefined, chave: string): string | null {
  return typeof valor === 'number' && valor > 0 ? tn(chave, valor) : null;
}

/**
 * Valor de um marcador `{x}`: primeiro o que a tela passou em `dados`, depois o
 * que o erro trouxe. `null` = não temos — a frase precisa degradar.
 */
function markerValue(
  key: string,
  error: ApiError | null,
  payload?: Record<string, string | undefined>,
): string | null {
  const screen = payload?.[key];
  if (screen) return screen;
  if (!error) return null;

  const data = error.data;
  switch (key) {
    case 'mensagem':
      return error.serverMessage || null;
    case 'tool':
      // `data.tool` é o extra de `tool_nao_catalogada` (§0). A tela também pode
      // passar `dados.tool` — o `daTela` acima ganha, porque quem conhece o
      // registro sabe o rótulo; o extra é a garantia de que a frase não degrade
      // quando ninguém passou nada.
      return data.tool || null;
    case 'version_executor':
      // `data.version_executor` é o extra de `versao_antiga_executor`: a versão que
      // o executor guarda. Sem ele a frase degrada para a versão sem número — dizer
      // "mais nova" sem dizer qual ainda é verdade, inventar o número não seria.
      return data.executor_version ? String(data.executor_version) : null;
    case 'identities':
      // `identities`/`resources` são os extras de `site_em_uso` (e de
      // `environment_in_use`, pelo mesmo motivo): o domínio manda os DOIS em toda
      // recusa — `domain.SiteInUsage` faz `WithData` dos dois e ainda escolhe entre
      // três frases. A tela não pode herdar a frase do servidor (ela vem só em
      // pt-BR), então repete a MESMA escolha sobre os mesmos números.
      //
      // Plural pelo CATÁLOGO (`tn`), nunca por ternário aqui dentro: pt-BR e en não
      // compartilham a regra, e foi exatamente assim que o marcador `minutos` vazou
      // português para o `en` até o F1-b o consertar, logo acima.
      return contagem(data.identities, 'common.count.identities');
    case 'resources':
      return contagem(data.resources, 'common.count.resources');
    case 'campos':
      return data.fields?.map((c) => c.field).join(', ') || null;
    case 'request_id':
      return data.request_id || null;
    case 'parameters':
      return (
        data.violations
          ?.flatMap((v) => v.parameters ?? [])
          .filter(Boolean)
          .join(', ') || null
      );
    case 'applications':
      return data.applications?.join(', ') || null;
    // aim-5wc — the extras of the chat entry refusals: the executor's `service`, `namespace` and `agent`, and the
    // product's `name` (the header whose secret did not open).
    case 'service':
      return data.service || null;
    case 'namespace':
      return data.namespace || null;
    case 'agent':
      return data.agent || null;
    case 'name':
      return data.name || null;
    case 'minutos': {
      // Arredonda para cima: "aguarde 1 minuto" é preferível a "aguarde 0".
      const seconds = data.retry_after_s;
      if (!seconds || seconds <= 0) return null;
      const minutes = Math.ceil(seconds / 60);
      // Plural pelo catálogo (`tn`), como todo texto: este era o único marcador que
      // conjugava em português dentro do código — e vazava «minutos» para o `en`.
      return tn('common.duration.minutes', minutes);
    }
    default:
      // `slug` não existe no `ErroData` do contrato: só chega por `dados`, da tela que
      // conhece o registro. `namespace` existe desde a entrada do chat (aim-5wc), acima.
      return null;
  }
}

/** Substitui os marcadores `{x}`; `completo: false` quando algum ficou sem valor. */
function interpolate(
  mensagem: string,
  error: ApiError | null,
  payload?: Record<string, string | undefined>,
): { texto: string; completo: boolean } {
  let complete = true;
  const texto = mensagem.replace(/\{(\w+)\}/g, (all, key: string) => {
    const valor = markerValue(key, error, payload);
    if (valor === null) {
      complete = false;
      return all;
    }
    return valor;
  });
  return { texto, completo: complete };
}

/**
 * Rede de segurança: tira os marcadores que sobraram sem deixar pontuação órfã.
 * Só roda para código sem `mensagemSemDados` — `errors.test.ts` garante que
 * nenhuma mensagem com `{x}` chegue aqui.
 */
function withoutMarkers(texto: string): string {
  return texto
    .replace(/«[^»]*\{\w+\}[^»]*»\s*/g, '')
    .replace(/:\s*\{\w+\}/g, '')
    .replace(/\{\w+\}/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,])/g, '$1')
    .trim();
}

/** Frase principal do erro, já degradada quando faltam os dados dos marcadores. */
function phrasePrincipal(
  spec: ErrorSpec,
  error: ApiError | null,
  payload?: Record<string, string | undefined>,
): string {
  const { texto, completo } = interpolate(spec.mensagem, error, payload);
  if (completo) return singleFullStop(texto);
  return spec.mensagemSemDados ?? withoutMarkers(texto);
}

/**
 * Tira o ponto final duplicado que nasce de uma frase do servidor interpolada
 * numa que já termina em ponto.
 *
 * Visto no cliente em 15/09: `error.campo_desconhecido` é `Dado inválido:
 * {mensagem}.` e o executor mandou uma `mensagem` que já acabava em ponto, então
 * o banner dizia «…usa o valor local.. Nada foi gravado.». A `{mensagem}` é texto
 * de OUTRO sistema — não se pode exigir que ela chegue sem pontuação —, por isso o
 * conserto é aqui e não no catálogo, e vale para toda frase com marcador de texto
 * livre.
 *
 * Dois pontos exatos, nunca três: reticências são pontuação legítima e ficam.
 */
function singleFullStop(texto: string): string {
  return texto.replace(/(?<!\.)\.\.(?!\.)/g, '.');
}

export interface ErrorViewOptions {
  /** `true` em operações de escrita: acrescenta "Nada foi gravado." (FR-123). */
  escrita?: boolean;
  /**
   * Contexto da tela para os marcadores que o `ErroData` não carrega
   * (`namespace`, `slug`, `tool`). Sem eles a frase usa `mensagemSemDados`.
   */
  dados?: Record<string, string | undefined>;
}

export interface ErrorView {
  code: string;
  /** Texto completo: mensagem + sufixo + "(código)". */
  texto: string;
  /** Só a mensagem, já interpolada e com o sufixo. */
  mensagem: string;
  efeito: ErrorEffect;
  link?: { rota: string; rotulo: string };
  /**
   * `X-Request-Id` da chamada ao middleware, quando o envelope o trouxe. É a
   * chave que liga o banner ao registro de auditoria (T23) e ao trace do
   * middleware — sem ela, "deu erro ao aprovar às 15h" é tudo que sobra para
   * quem for investigar depois.
   */
  requestId?: string;
  /**
   * `false` quando a `mensagem` **já nomeia** o código — caso da frase montada
   * para código fora da tabela ("O middleware respondeu «x»: …"). Quem exibe
   * acrescenta o `(code)` só quando isto é `true`; sem a marca, o banner
   * mostraria o mesmo código duas vezes na mesma linha.
   */
  mostrarCodigo: boolean;
}

/**
 * Códigos cuja frase muda com as CONTAGENS do `data` — hoje só `site_em_uso`.
 *
 * # POR QUE TRÊS FRASES, E NÃO UMA COM DOIS NÚMEROS
 *
 * O domínio já escolhe entre três (`domain.SiteInUsage`), e por duas razões que a
 * tela herda: «Este site tem 4 identidades e 0 recursos» lê como defeito, e a
 * concordância muda («Remova-AS» com identidades, «Remova-OS» com recursos). Repetir
 * a escolha aqui é o preço de a tela falar o idioma do usuário: a `message` do
 * servidor vem só em pt-BR, e o console tem `en`.
 *
 * `null` = nenhuma contagem veio (backend anterior ao `ErroData` com os campos, ou
 * `data` vazio) — e aí vale a frase BASE do catálogo, que é a antiga, sem número.
 * É a mesma degradação de `mensagemSemDados`, pela mesma razão.
 *
 * `environment_in_use` é o irmão gêmeo deste caso (o contrato declara os dois campos
 * para os dois códigos) e entra aqui no dia em que a tela de ambientes quiser as
 * contagens: uma linha na tabela e três chaves no catálogo, sem tocar nesta lógica.
 */
const VARIANTE_POR_CONTAGEM: Record<string, true> = { site_em_uso: true };

function variantOf(code: string, data: ApiError['data']): string | null {
  if (!VARIANTE_POR_CONTAGEM[code]) return null;
  const identities = data.identities ?? 0;
  const resources = data.resources ?? 0;
  if (identities > 0 && resources > 0) return 'both';
  if (identities > 0) return 'onlyIdentities';
  if (resources > 0) return 'onlyResources';
  return null;
}

/**
 * Traduz um `ApiError` no que o `ErrorBanner` mostra (FR-123):
 * mensagem amigável + código técnico entre parênteses + "Nada foi gravado."
 */
export function errorView(error: ApiError | string, options: ErrorViewOptions = {}): ErrorView {
  const apiError = typeof error === 'string' ? null : error;
  const code = typeof error === 'string' ? error : error.code;
  // A `message` do servidor entra na escolha do texto, não só na interpolação:
  // é ela que deixa um código fora da tabela se explicar em vez de virar
  // "não foi possível concluir a operação" (`especDeCodigoNovo`).
  const serverMessage = apiError?.serverMessage;
  const spec = errorSpec(code, serverMessage);

  // `estado_parcial` sempre orienta a reconciliar, mas quem dá a frase é o código
  // original do middleware.
  const phraseSpec =
    code === 'estado_parcial' && apiError?.data.original_code
      ? errorSpec(apiError.data.original_code, serverMessage)
      : spec;

  /**
   * A frase de gravação é decidida por três coisas, nesta ordem (FR-123):
   * o que o middleware relatou (`desfeito: false` = pode ter sobrado estado),
   * o que o código significa (`gravacao`) e, por último, se quem chamou disse que
   * era escrita. As partes são atômicas — nenhum sufixo carrega "Nada foi gravado."
   * embutido, senão o filtro de estado parcial não teria como removê-la.
   */
  const canTerStored = apiError?.desfeito === false || spec.gravacao === 'parcial';
  const saysNothingStored =
    !canTerStored &&
    (spec.gravacao === 'nada' || (options.escrita === true && spec.efeito !== 'login'));

  // A variante entra DEPOIS da espec e ANTES da interpolação: é a mesma frase
  // principal, só que a escolhida pelas contagens — os `{identities}`/`{resources}`
  // dela resolvem no `markerValue`, como qualquer outro marcador. O código consultado
  // é o da FRASE: em `estado_parcial` quem a dá é o `original_code`, e a variante
  // segue-o para não descrever um erro com a frase de outro.
  const phraseCode =
    code === 'estado_parcial' && apiError?.data.original_code ? apiError.data.original_code : code;
  const variante = apiError ? variantOf(phraseCode, apiError.data) : null;
  const chaveVariante = variante ? `error.${phraseCode}.${variante}` : '';
  const finalSpec =
    chaveVariante && hasKey(chaveVariante) ? { ...phraseSpec, mensagem: t(chaveVariante) } : phraseSpec;

  const parts = [phrasePrincipal(finalSpec, apiError, options.dados)];
  if (saysNothingStored) parts.push(nothingStored());
  if (spec.sufixo) parts.push(spec.sufixo);

  const mensagem = parts.filter(Boolean).join(' ').trim();

  const mostrarCode = spec.codigoNaFrase !== true;

  return {
    code,
    mensagem,
    texto: mostrarCode ? `${mensagem} (${code})` : mensagem,
    efeito: spec.efeito,
    mostrarCodigo: mostrarCode,
    ...(spec.link ? { link: spec.link } : {}),
    ...(apiError?.data.request_id ? { requestId: apiError.data.request_id } : {}),
  };
}
